// The task's report wired to the API (design §7): loads the report, renders
// the chosen version (with `assetBase`, see reportsApi.render), lists the
// submissions, sends `goblic.submit` data after confirmation and downloads
// the export. Used by the task drawer's Report tab and the full-screen route.
//
// The version on screen never changes by itself — someone may be reading or
// filling it in. A newer version shows "New version vN available — view";
// expired image links show "Images expired — reload report".

import { useEffect, useState, type ReactNode } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Notice } from '../ui'
import {
  useDownloadReport,
  useLastOwnSubmission,
  useReportPermissions,
  useReportRender,
  useReportSubmissions,
  useSubmitReport,
  useReportUrlsExpired,
  useTaskReport,
} from '../../hooks/useReports'
import { ReportPanel } from './report-panel'

export function TaskReport({
  taskId,
  variant = 'inline',
  live = false,
  actions,
}: {
  taskId: string
  variant?: 'inline' | 'fullscreen'
  /** Poll for new versions (the task has a run going). */
  live?: boolean
  actions?: ReactNode
}) {
  const navigate = useNavigate()
  const { canSubmit } = useReportPermissions()
  const reportQuery = useTaskReport(taskId, {
    refetchInterval: live ? 15_000 : false,
  })
  const report = reportQuery.data?.report ?? null
  // The version on screen; set once the report loads, then only by the viewer.
  const [selected, setSelected] = useState<number | null>(null)
  // True while the viewer is on what was the latest version when they chose it.
  const [onLatest, setOnLatest] = useState(true)
  useEffect(() => {
    setSelected(null)
    setOnLatest(true)
  }, [taskId])
  useEffect(() => {
    if (selected == null && report && report.currentVersion > 0) {
      setSelected(report.currentVersion)
    }
  }, [selected, report])
  const version = selected ?? report?.currentVersion ?? null
  const newerVersion =
    report && version != null && onLatest && report.currentVersion > version
      ? report.currentVersion
      : null

  const renderQuery = useReportRender(report?.id, version)
  const submissionsQuery = useReportSubmissions(
    variant === 'inline' ? report?.id : null
  )
  const submit = useSubmitReport(report?.id, taskId)
  const lastOwnSubmission = useLastOwnSubmission(report?.id)
  const { download, downloading, error: downloadError } = useDownloadReport()

  // Keep the previous document on screen while another version loads only
  // if it is that version; otherwise show the loading state.
  const render =
    renderQuery.data && renderQuery.data.version === version
      ? renderQuery.data
      : null
  const imagesExpired = useReportUrlsExpired(
    render && render.assets.length > 0 ? render.urlExpiresAt : null
  )

  return (
    <div className="space-y-3">
      <ReportPanel
        report={report}
        render={render}
        loading={reportQuery.isLoading || renderQuery.isLoading}
        error={reportQuery.error ?? renderQuery.error ?? undefined}
        versions={report?.versions ?? []}
        selectedVersion={version ?? undefined}
        onSelectVersion={(next) => {
          setSelected(next)
          setOnLatest(next === report?.currentVersion)
        }}
        submissions={submissionsQuery.data?.submissions ?? []}
        onSubmit={submit}
        canSubmit={canSubmit}
        onLastSubmission={lastOwnSubmission}
        notices={
          <>
            {newerVersion != null ? (
              <Notice tone="info">
                <span className="flex flex-wrap items-center gap-2">
                  New version v{newerVersion} available —
                  <button
                    type="button"
                    className="link"
                    onClick={() => {
                      setSelected(newerVersion)
                      setOnLatest(true)
                    }}
                  >
                    view
                  </button>
                </span>
              </Notice>
            ) : null}
            {imagesExpired ? (
              <Notice tone="signal">
                <span className="flex flex-wrap items-center gap-2">
                  Images expired —
                  <button
                    type="button"
                    className="link"
                    onClick={() => void renderQuery.refetch()}
                    disabled={renderQuery.isFetching}
                  >
                    {renderQuery.isFetching ? 'reloading…' : 'reload report'}
                  </button>
                </span>
              </Notice>
            ) : null}
          </>
        }
        onFullScreen={() =>
          void navigate({ to: '/tasks/$taskId/report', params: { taskId } })
        }
        onDownload={
          report && version
            ? () => void download(report.id, version, slugify(report.title))
            : undefined
        }
        downloading={downloading}
        variant={variant}
        actions={actions}
      />
      {render && render.missingAssets.length > 0 ? (
        <Notice tone="signal">
          This version refers to attachments that don’t exist:{' '}
          {render.missingAssets.join(', ')}.
        </Notice>
      ) : null}
      {downloadError ? (
        <Notice tone="error">
          Couldn’t download the report:{' '}
          {downloadError instanceof Error
            ? downloadError.message
            : String(downloadError)}
        </Notice>
      ) : null}
    </div>
  )
}

function slugify(title: string): string {
  return (
    title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'report'
  )
}
